import React, { useState, useEffect } from 'react';
import { 
  Wifi, Globe, Zap, MapPin, Phone, CheckCircle, ChevronDown,
  ArrowRight, Signal, Shield, Wrench, Sun, Battery, Home, 
  Tractor, Users, Settings, Cable, Satellite
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import FloatingButtons from '../components/FloatingButtons';

const InternetSatelitalPage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const starlinkImages = {
    terminal: 'https://images.unsplash.com/photo-1702843431695-68282d907e0f?w=800',
    dish: 'https://images.unsplash.com/photo-1756573346001-6c3ab30e837b?w=800',
    logo: 'https://images.unsplash.com/photo-1652017681821-61cd1a8ad37b?w=600'
  };

  const serviceIncludes = [
    {
      icon: MapPin,
      title: 'Ubicación Ideal',
      description: 'Instalamos la antena Starlink en la posición perfecta para máxima señal, buscando el mejor ángulo de cielo abierto.'
    },
    {
      icon: Cable,
      title: 'Cableado Profesional',
      description: 'Realizamos el tendido de cable desde la antena hasta el interior de tu vivienda de forma prolija y segura.'
    },
    {
      icon: Settings,
      title: 'Configuración Completa',
      description: 'Configuramos tu cuenta Starlink, métodos de pago, y dejamos todo listo para que solo tengas que conectarte.'
    },
    {
      icon: Users,
      title: 'Capacitación',
      description: 'Te enseñamos a usar la app, gestionar tu red WiFi, y resolver problemas básicos por tu cuenta.'
    }
  ];

  const solarCombo = [
    {
      icon: Sun,
      title: 'Paneles Solares Dedicados',
      description: 'Sistema de paneles exclusivo para tu antena Starlink, dimensionado para su consumo específico.'
    },
    {
      icon: Battery,
      title: 'Baterías de Respaldo',
      description: 'Almacenamiento de energía para que tu internet siga funcionando de noche y durante cortes de luz.'
    },
    {
      icon: Shield,
      title: 'Internet 24/7 Garantizado',
      description: 'Nunca más te quedás sin internet. Independencia total de la red eléctrica.'
    }
  ];

  const idealFor = [
    {
      icon: Tractor,
      title: 'Zonas Rurales y Estancias',
      description: 'Donde la fibra óptica no llega. Conectá tu campo, estancia o chacra al mundo.',
      highlight: true
    },
    {
      icon: Home,
      title: 'Casas de Campo',
      description: 'Internet de alta velocidad para tu fin de semana o residencia rural.'
    },
    {
      icon: Globe,
      title: 'Cualquier Punto de Paraguay',
      description: 'También disponible en ciudades donde la conexión tradicional no es buena.'
    }
  ];

  const faqs = [
    {
      question: '¿Qué es Starlink y por qué es tan bueno?',
      answer: 'Starlink es el servicio de internet satelital de SpaceX, con miles de satélites en órbita baja. Esto significa velocidades altísimas (hasta 200 Mbps) y baja latencia, similar a la fibra óptica. Incluso con el plan más básico, podés trabajar, ver series en HD, hacer videollamadas y tener a toda tu familia conectada al mismo tiempo sin problemas.'
    },
    {
      question: '¿El internet satelital funciona con energía solar?',
      answer: 'Sí, es la combinación perfecta. La antena Starlink consume aproximadamente 75-100W, lo cual es muy fácil de alimentar con un pequeño sistema solar. Ofrecemos la instalación del kit solar + baterías exclusivo para tu internet, así nunca te quedás sin conexión.'
    },
    {
      question: '¿Funciona cuando llueve o está nublado?',
      answer: 'Sí, el servicio funciona en condiciones climáticas normales. Solo tormentas muy severas pueden afectar temporalmente la señal. La antena tiene diseño resistente a la intemperie y se calienta automáticamente para derretir nieve o hielo.'
    },
    {
      question: '¿Cuánto demora la instalación?',
      answer: 'La instalación completa (antena, cableado, configuración y capacitación) toma aproximadamente 2-4 horas dependiendo de las características de tu propiedad. Coordinamos la visita según tu disponibilidad.'
    },
    {
      question: '¿Puedo llevar el equipo si me mudo?',
      answer: 'Sí, el equipo es tuyo y portátil. Puede trasladarse a cualquier ubicación. Solo necesitás actualizar la dirección en la app de Starlink. También ofrecemos servicio de reinstalación si lo necesitás.'
    },
    {
      question: '¿Cuánto cuesta el servicio?',
      answer: 'Los costos varían según el kit Starlink que elijas y si querés agregar el combo solar. Te recomendamos contactarnos para una cotización personalizada según tus necesidades. Lo que sí podemos decirte es que incluso el plan más básico de Starlink ofrece velocidades muy altas para todo tipo de uso.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation isScrolled={isScrolled} />

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950"></div>
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500 rounded-full filter blur-[100px]"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-emerald-500 rounded-full filter blur-[120px]"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/20 rounded-full mb-6">
              <Wifi className="w-4 h-4 text-blue-400" />
              <span className="text-sm font-medium text-blue-400">Instaladores Starlink</span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Internet de Alta Velocidad
              <span className="text-[#90EE90]"> en Cualquier Lugar</span>
            </h1>
            
            <p className="text-base sm:text-lg md:text-xl text-zinc-300 mb-8 leading-relaxed">
              Somos instaladores oficiales de antenas <strong className="text-white">Starlink</strong>. 
              Te instalamos, configuramos y dejamos funcionando tu internet satelital. 
              <span className="text-emerald-400"> Ideal para el campo y zonas sin fibra óptica.</span>
            </p>

            {/* Key benefit */}
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 mb-8">
              <p className="text-zinc-200">
                <strong className="text-emerald-400">Con el plan más básico</strong> ya tenés velocidad suficiente para 
                trabajar, ver series en HD, hacer videollamadas y tener a toda la familia conectada al mismo tiempo. 
                Es un sistema muy potente.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="https://wa.me/595991344956?text=Hola%20Zenex!%20Me%20interesa%20el%20servicio%20de%20instalación%20de%20Starlink"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-8 py-4 bg-[#90EE90] hover:bg-[#7DD87D] text-zinc-900 font-bold rounded-xl transition-all duration-300 hover:scale-105"
              >
                Solicitar Instalación
                <ArrowRight size={20} />
              </a>
              <a
                href="#combo-solar"
                className="flex items-center justify-center gap-2 px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl transition-all duration-300 border border-white/20"
              >
                <Sun size={20} />
                Ver Combo Solar
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* What is Starlink - Futuristic Section */}
      <section className="py-16 md:py-24 bg-zinc-950 relative overflow-hidden">
        {/* Futuristic background elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-emerald-500/50 to-transparent"></div>
          <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-blue-500/50 to-transparent"></div>
          <div className="absolute top-1/4 left-10 w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
          <div className="absolute top-1/3 right-20 w-1 h-1 bg-blue-400 rounded-full animate-pulse"></div>
          <div className="absolute bottom-1/4 left-1/4 w-1.5 h-1.5 bg-emerald-300 rounded-full animate-pulse"></div>
          <div className="absolute top-1/2 right-1/3 w-1 h-1 bg-white/50 rounded-full animate-pulse"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left side - Content */}
            <div>
              <div className="flex items-center gap-4 mb-6">
                {/* Zenex Logo representation */}
                <div className="w-14 h-14 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/30">
                  <span className="text-2xl font-black text-white">Z</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs uppercase tracking-widest text-zinc-500">Powered by</span>
                  <span className="text-white font-bold">Starlink</span>
                </div>
              </div>
              
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
                La Red del
                <span className="bg-gradient-to-r from-emerald-400 to-blue-400 bg-clip-text text-transparent"> Futuro</span>
                <br />Ya Está Aquí
              </h2>
              
              <p className="text-zinc-400 text-lg mb-6 leading-relaxed">
                <strong className="text-white">Starlink</strong> es la constelación de satélites de internet más avanzada del mundo, 
                creada por SpaceX. Miles de satélites en órbita baja trabajan juntos para llevar internet 
                de alta velocidad a cualquier punto del planeta.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4 p-4 bg-zinc-900/50 rounded-xl border border-zinc-800">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Satellite className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">+6.000 satélites en órbita</h4>
                    <p className="text-zinc-500 text-sm">La red satelital más grande de la historia de la humanidad</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 p-4 bg-zinc-900/50 rounded-xl border border-zinc-800">
                  <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Zap className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">Órbita baja = Baja latencia</h4>
                    <p className="text-zinc-500 text-sm">Tan rápido como la fibra óptica, pero sin cables</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 p-4 bg-zinc-900/50 rounded-xl border border-zinc-800">
                  <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Globe className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">Cobertura global</h4>
                    <p className="text-zinc-500 text-sm">Funciona en cualquier lugar donde veas el cielo</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-r from-emerald-500/10 to-blue-500/10 border border-emerald-500/20 rounded-xl p-5">
                <p className="text-zinc-300 text-sm leading-relaxed">
                  <strong className="text-emerald-400">En Zenex</strong> somos pioneros en Paraguay llevando esta tecnología a hogares 
                  y empresas. Trabajamos con equipamiento de primer nivel mundial porque creemos que nuestros clientes 
                  merecen lo mejor. <span className="text-white">El futuro ya llegó, y nosotros te conectamos.</span>
                </p>
              </div>
            </div>
            
            {/* Right side - Visual */}
            <div className="relative">
              {/* Animated orbit lines */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-72 h-72 border border-zinc-800 rounded-full animate-spin" style={{animationDuration: '20s'}}></div>
                <div className="absolute w-56 h-56 border border-zinc-800/50 rounded-full animate-spin" style={{animationDuration: '15s', animationDirection: 'reverse'}}></div>
                <div className="absolute w-40 h-40 border border-emerald-500/20 rounded-full animate-spin" style={{animationDuration: '10s'}}></div>
              </div>
              
              {/* Center content */}
              <div className="relative z-10 bg-zinc-900/80 backdrop-blur-sm rounded-3xl p-8 border border-zinc-800">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 rounded-full mb-4">
                    <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
                    <span className="text-emerald-400 text-sm font-medium">Tecnología de SpaceX</span>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">¿Por qué Starlink?</h3>
                </div>
                
                <div className="space-y-3">
                  {[
                    { text: 'Velocidades de más de 300 Mbps', highlight: true },
                    { text: 'Latencia similar a fibra óptica' },
                    { text: 'Sin infraestructura terrestre' },
                    { text: 'Instalación en menos de 1 hora' },
                    { text: 'Funciona en tormentas y lluvia' },
                    { text: 'Actualizaciones automáticas' }
                  ].map((item, index) => (
                    <div key={index} className={`flex items-center gap-3 p-3 rounded-lg ${item.highlight ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-zinc-800/50'}`}>
                      <CheckCircle className={`w-5 h-5 flex-shrink-0 ${item.highlight ? 'text-emerald-400' : 'text-zinc-500'}`} />
                      <span className={item.highlight ? 'text-emerald-400 font-semibold' : 'text-zinc-300'}>{item.text}</span>
                    </div>
                  ))}
                </div>
                
                <div className="mt-6 pt-6 border-t border-zinc-800 text-center">
                  <p className="text-zinc-500 text-sm mb-2">Más de 4 millones de usuarios en el mundo</p>
                  <p className="text-white font-bold">Sumate a la revolución</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Starlink Product Showcase */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-zinc-900 mb-3">
              Conocé el Equipo Starlink
            </h2>
            <p className="text-zinc-600">
              Diseño moderno, compacto y resistente a la intemperie
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {/* Main dish image */}
            <div className="md:col-span-2 relative rounded-2xl overflow-hidden group">
              <img 
                src={starlinkImages.terminal}
                alt="Antena Starlink instalada"
                className="w-full h-64 md:h-80 object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              <div className="absolute bottom-0 left-0 p-6">
                <h3 className="text-xl font-bold text-white mb-1">Antena Starlink Standard</h3>
                <p className="text-white/80 text-sm">La última generación: compacta, potente y fácil de instalar</p>
              </div>
            </div>
            
            {/* Side images */}
            <div className="space-y-6">
              <div className="relative rounded-2xl overflow-hidden group">
                <img 
                  src={starlinkImages.dish}
                  alt="Starlink dish"
                  className="w-full h-36 object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-0 left-0 p-4">
                  <p className="text-white font-medium text-sm">Instalación en exteriores</p>
                </div>
              </div>
              
              <div className="bg-zinc-900 rounded-2xl p-6 text-center">
                <Wifi className="w-10 h-10 text-[#90EE90] mx-auto mb-3" />
                <p className="text-white font-bold text-lg mb-1">+300 Mbps</p>
                <p className="text-zinc-400 text-sm">Velocidad de descarga</p>
              </div>
            </div>
          </div>
          
          {/* Features strip */}
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Diseño compacto', desc: 'Antena plana moderna' },
              { label: 'Resistente', desc: 'Lluvia, nieve, calor' },
              { label: 'Fácil instalación', desc: 'Orientación manual' },
              { label: 'WiFi 6 incluido', desc: 'Router de última gen.' }
            ].map((feature, index) => (
              <div key={index} className="bg-zinc-50 rounded-xl p-4 text-center">
                <p className="font-bold text-zinc-900">{feature.label}</p>
                <p className="text-xs text-zinc-500">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Includes */}
      <section className="py-16 md:py-24 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-sm font-medium uppercase tracking-widest text-[#90EE90] mb-4 block">
              Servicio de Instalación
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              ¿Qué Incluye Nuestro Servicio?
            </h2>
            <p className="text-zinc-600 max-w-2xl mx-auto">
              Nos encargamos de todo para que vos solo tengas que conectarte y disfrutar
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {serviceIncludes.map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className="bg-white p-6 rounded-2xl border border-zinc-200 hover:shadow-xl transition-all duration-300">
                  <div className="p-3 bg-blue-100 rounded-xl w-fit mb-4">
                    <Icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-bold text-zinc-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-zinc-600">{item.description}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-12 bg-white p-6 sm:p-8 rounded-2xl border border-zinc-200">
            <h3 className="text-xl font-bold text-zinc-900 mb-4 flex items-center gap-2">
              <Wrench className="w-5 h-5 text-[#90EE90]" />
              El proceso es simple:
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { step: '1', text: 'Nos contactás y coordinamos la visita' },
                { step: '2', text: 'Instalamos la antena en la posición ideal' },
                { step: '3', text: 'Hacemos el cableado hasta tu casa' },
                { step: '4', text: 'Configuramos tu cuenta y te capacitamos' }
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-[#90EE90] rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-zinc-900">{item.step}</span>
                  </div>
                  <p className="text-sm text-zinc-600 pt-1">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Ideal For - Rural Focus */}
      <section className="py-16 md:py-24 bg-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-sm font-medium uppercase tracking-widest text-[#90EE90] mb-4 block">
              ¿Para Quién es?
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
              Especialmente para Zonas Rurales
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              Donde la fibra óptica no llega, Starlink es la solución perfecta
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {idealFor.map((item, index) => {
              const Icon = item.icon;
              return (
                <div 
                  key={index} 
                  className={`p-6 rounded-2xl transition-all duration-300 ${
                    item.highlight 
                      ? 'bg-[#90EE90]/20 border-2 border-[#90EE90]' 
                      : 'bg-zinc-800 border border-zinc-700 hover:border-[#90EE90]/50'
                  }`}
                >
                  <div className={`p-3 rounded-xl w-fit mb-4 ${item.highlight ? 'bg-[#90EE90]/30' : 'bg-zinc-700'}`}>
                    <Icon className={`w-6 h-6 ${item.highlight ? 'text-[#90EE90]' : 'text-zinc-300'}`} />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                  <p className={`text-sm ${item.highlight ? 'text-zinc-300' : 'text-zinc-400'}`}>{item.description}</p>
                  {item.highlight && (
                    <div className="mt-4 inline-flex items-center gap-2 text-[#90EE90] text-sm font-medium">
                      <CheckCircle className="w-4 h-4" />
                      Más común en zonas rurales
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Solar Combo */}
      <section id="combo-solar" className="py-16 md:py-24 bg-gradient-to-br from-[#14532D] to-[#166534]">
        <div className="max-w-7xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full mb-6">
                <Zap className="w-4 h-4 text-[#90EE90]" />
                <span className="text-sm font-medium text-[#90EE90]">Combo Exclusivo</span>
              </div>
              
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-6">
                Internet + Energía Solar = 
                <span className="text-[#90EE90]"> Nunca Más Sin Conexión</span>
              </h2>
              
              <p className="text-white/80 mb-8 leading-relaxed text-lg">
                Combiná tu antena Starlink con un sistema de paneles solares y baterías 
                <strong className="text-white"> exclusivo para tu internet</strong>. 
                Así, aunque se corte la luz, tu conexión sigue funcionando.
              </p>
              
              <div className="space-y-4 mb-8">
                {solarCombo.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="flex items-start gap-4 bg-white/10 p-4 rounded-xl">
                      <div className="p-2 bg-[#90EE90]/20 rounded-lg">
                        <Icon className="w-5 h-5 text-[#90EE90]" />
                      </div>
                      <div>
                        <h3 className="font-bold text-white mb-1">{item.title}</h3>
                        <p className="text-sm text-white/70">{item.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <a
                href="https://wa.me/595991344956?text=Hola%20Zenex!%20Me%20interesa%20el%20combo%20de%20Starlink%20con%20energía%20solar"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#90EE90] text-zinc-900 font-bold rounded-xl hover:bg-[#7CDF7C] transition-colors"
              >
                <Phone className="w-5 h-5" />
                Consultar Combo Solar
              </a>
            </div>
            
            <div className="relative">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <h3 className="text-xl font-bold text-white mb-6 text-center">Beneficios del Combo</h3>
                <div className="space-y-4">
                  {[
                    'Internet funcionando 24/7, llueva o truene',
                    'Independencia total de la red eléctrica',
                    'Ideal para estancias y casas de campo',
                    'Sistema dimensionado exactamente para Starlink',
                    'Instalación profesional incluida',
                    'Garantía en todos los equipos'
                  ].map((benefit, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-[#90EE90] flex-shrink-0" />
                      <span className="text-white/90">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Image below benefits */}
              <div className="mt-6 rounded-2xl overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1509391366360-2e959784a276?w=600"
                  alt="Energía solar en el campo"
                  className="w-full h-48 object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-zinc-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-sm font-medium uppercase tracking-widest text-[#90EE90] mb-4 block">
              Preguntas Frecuentes
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-zinc-900">
              Sobre Internet Starlink
            </h2>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-zinc-200 rounded-xl overflow-hidden bg-white">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-zinc-50 transition-colors"
                >
                  <span className="font-semibold text-zinc-900 pr-4 text-sm sm:text-base">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-zinc-500 transition-transform flex-shrink-0 ${openFaq === index ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-5">
                    <p className="text-zinc-600 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-blue-800 to-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12 text-center">
          <Wifi className="w-12 h-12 text-blue-200 mx-auto mb-6" />
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
            ¿Querés Internet en el Campo?
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Consultanos sobre la instalación de Starlink. Te asesoramos y te damos una cotización personalizada.
          </p>
          <a
            href="https://wa.me/595991344956?text=Hola%20Zenex!%20Quiero%20consultar%20sobre%20instalación%20de%20Starlink"
            target="_blank"
            rel="noopener noreferrer"
            className="px-8 py-4 bg-white text-blue-800 font-bold rounded-xl hover:bg-zinc-100 transition-colors inline-flex items-center gap-2"
          >
            <Phone className="w-5 h-5" />
            Solicitar Cotización
          </a>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default InternetSatelitalPage;
